import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  Pagination,
  Spinner,
} from "react-bootstrap";
import { FaChartBar, FaSync, FaTimes, FaTrash, FaEdit } from "react-icons/fa";
import { subscriptionsService } from "../../services/subscriptionsService";
import { showToast, getErrorMessage } from "../../utils/toast";

const SubscriptionsTab = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [planStats, setPlanStats] = useState([]);
  const [overallStats, setOverallStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deletingSubscription, setDeletingSubscription] = useState(false);

  // Filter and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSubscriptionsData = useCallback(async () => {
    try {
      setLoading(true);
      setIsSearching(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(planFilter !== "all" && { plan: planFilter }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      };

      const response = await subscriptionsService.getAllSubscriptions(params);
      setSubscriptions(response.subscriptions || []);
      setPagination(response.pagination || {});
      setError(null);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Failed to load subscriptions");
      showToast.error("Failed to load subscriptions: " + getErrorMessage(err));
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, statusFilter, planFilter, debouncedSearchTerm]);

  const fetchSubscriptionStats = useCallback(async () => {
    try {
      const response = await subscriptionsService.getSubscriptionStats();
      setPlanStats(response.planStats || []);
      setOverallStats(response.overallStats || {});
    } catch (err) {
      console.error("Error fetching subscription stats:", err);
      showToast.error("Failed to load subscription statistics");
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionsData();
    fetchSubscriptionStats();
  }, [fetchSubscriptionsData, fetchSubscriptionStats]);

  const handleStatusChange = async () => {
    if (!selectedSubscription || !newStatus) return;

    setUpdatingStatus(true);
    try {
      await subscriptionsService.updateSubscriptionStatus(
        selectedSubscription.id,
        newStatus,
        statusReason
      );

      showToast.success(`Subscription status updated to ${newStatus}`);
      setShowStatusModal(false);
      setSelectedSubscription(null);
      setNewStatus("");
      setStatusReason("");

      // Refresh data
      fetchSubscriptionsData();
      fetchSubscriptionStats();
    } catch (error) {
      console.error("Error updating subscription status:", error);
      showToast.error(
        "Failed to update subscription status: " + getErrorMessage(error)
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      cancelled: "danger",
      expired: "warning",
      suspended: "secondary",
      created: "info",
    };
    return (
      <Badge bg={variants[status] || "secondary"}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const getPlanBadge = (plan) => {
    const variants = {
      FREE: "warning",
      BASIC: "info",
      ESSENTIAL: "primary",
      PRO: "success",
      ULTIMATE: "danger",
      CUSTOM: "dark",
    };
    return <Badge bg={variants[plan] || "secondary"}>{plan}</Badge>;
  };

  const formatCurrency = (amount, currency = "INR") => {
    if (!amount || amount === null || amount === undefined) return "N/A";
    return `${currency} ${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === null || dateString === "null")
      return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "N/A";
    }
  };

  const formatValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "null" ||
      value === ""
    ) {
      return "N/A";
    }
    return value;
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, planFilter]);

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  const handleDeleteSubscription = async () => {
    if (!selectedSubscription?.id) return;

    setDeletingSubscription(true);
    try {
      await subscriptionsService.deleteSubscription(selectedSubscription.id);
      showToast.success(`Deleted subscription #${selectedSubscription.id}`);
      setShowDeleteModal(false);
      setSelectedSubscription(null);
      await fetchSubscriptionsData();
      await fetchSubscriptionStats();
    } catch (err) {
      console.error("Error deleting subscription:", err);
      showToast.error("Failed to delete subscription: " + getErrorMessage(err));
    } finally {
      setDeletingSubscription(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-3">Subscriptions Management</h2>
              <p className="text-muted">
                Manage user subscriptions and monitor plan usage.
              </p>
            </div>
            <Button
              variant="outline-primary"
              onClick={() => {
                fetchSubscriptionsData();
                fetchSubscriptionStats();
              }}
            >
              <FaSync className="me-2" />
              Refresh
            </Button>
          </div>
        </Col>
      </Row>

      {/* Plan Statistics */}
      <Row className="mb-4">
        {planStats.map((stat, index) => (
          <Col key={index} lg={4} md={6} className="mb-3">
            <Card className={`border-${stat.color} h-100`}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title text-muted">{stat.plan} Plan</h6>
                    <h3 className="mb-1">{stat.count}</h3>
                    <p className="text-muted mb-0">Subscribers</p>
                    <h6 className="text-success mt-2 d-none">{stat.revenue}</h6>
                    <small className="text-muted">
                      Avg Usage: {stat.avgUsage}%
                    </small>
                  </div>
                  <div className={`text-${stat.color} fs-1`}>
                    <FaChartBar />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}

        {/* Overall Stats Card */}
        <Col lg={4} md={6} className="mb-3">
          <Card className="border-info h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title text-muted">Total Subscriptions</h6>
                  <h3 className="mb-1">
                    {overallStats.total_subscriptions || 0}
                  </h3>
                  <p className="text-muted mb-0">All Time</p>
                  <div className="mt-2">
                    <small className="text-success">
                      Active: {overallStats.active_subscriptions || 0}
                    </small>
                    <br />
                    <small className="text-danger">
                      Cancelled: {overallStats.cancelled_subscriptions || 0}
                    </small>
                  </div>
                </div>
                <div className="text-info fs-1">
                  <FaChartBar />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search users, emails, plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={isSearching ? "pe-5" : searchTerm ? "pe-5" : ""}
              />
              {searchTerm && (
                <Button
                  variant="link"
                  className="position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                  onClick={handleClearSearch}
                  style={{ zIndex: 10 }}
                >
                  <FaTimes size={12} />
                </Button>
              )}
              {isSearching && !searchTerm && (
                <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
            {searchTerm && (
              <small className="text-muted">
                Searching for: "{searchTerm}"
              </small>
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status Filter</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Plan Filter</Form.Label>
            <Form.Select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <option value="all">All Plans</option>
              <option value="FREE">Free</option>
              <option value="BASIC">Basic</option>
              <option value="ESSENTIAL">Essential</option>
              <option value="PRO">Pro</option>
              <option value="ULTIMATE">Ultimate</option>
              <option value="CUSTOM">Custom</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setPlanFilter("all");
              setCurrentPage(1);
            }}
            className="w-100"
          >
            <FaTimes className="me-2" />
            Clear All Filters
          </Button>
        </Col>
      </Row>

      {/* Subscriptions Table */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Subscriptions ({pagination.totalSubscriptions || 0})
            </h5>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading subscriptions...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger text-center" role="alert">
              {error}
              <Button
                variant="outline-primary"
                className="ms-2"
                onClick={fetchSubscriptionsData}
              >
                Retry
              </Button>
            </div>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Billing Date</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((subscription) => (
                    <tr key={subscription.id}>
                      <td>{subscription.id}</td>
                      <td>{formatValue(subscription.user_name)}</td>
                      <td>{formatValue(subscription.email)}</td>
                      <td>{formatDate(subscription.created_at)}</td>
                      <td>{getPlanBadge(subscription.plan_name)}</td>
                      <td>{getStatusBadge(subscription.status)}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Change subscription status"
                            onClick={() => {
                              setSelectedSubscription(subscription);
                              setNewStatus(
                                String(subscription.status || "active").toLowerCase()
                              );
                              setStatusReason("");
                              setShowStatusModal(true);
                            }}
                          >
                            <FaEdit className="me-1" />
                            Status
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={
                              String(subscription.status).toLowerCase() === "active"
                            }
                            title={
                              String(subscription.status).toLowerCase() === "active"
                                ? "Cancel/suspend first, then delete"
                                : "Delete subscription"
                            }
                            onClick={() => {
                              setSelectedSubscription(subscription);
                              setShowDeleteModal(true);
                            }}
                          >
                            <FaTrash className="me-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted">
            Showing {(currentPage - 1) * pagination.limit + 1} to{" "}
            {Math.min(
              currentPage * pagination.limit,
              pagination.totalSubscriptions
            )}{" "}
            of {pagination.totalSubscriptions} subscriptions
          </div>
          <Pagination className="mb-0">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />

            {Array.from({ length: pagination.totalPages }, (_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === pagination.totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}

            <Pagination.Next
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(pagination.totalPages)}
              disabled={currentPage === pagination.totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Subscription Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubscription && (
            <Form>
              <div className="mb-3">
                <strong>Subscription:</strong> {selectedSubscription.user_name}{" "}
                - {selectedSubscription.plan_name}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>New Status</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                  <option value="halted">Halted</option>
                  <option value="pending_payment">Pending Payment</option>
                  <option value="suspended">Suspended</option>
                  <option value="created">Created</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Reason (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Enter reason for status change..."
                />
              </Form.Group>
              {(newStatus === "cancelled" || newStatus === "suspended") && (
                <div className="alert alert-warning">
                  <strong>Warning:</strong> This will revert the user to 'none'
                  plan with 0 API limit.
                </div>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleStatusChange}
            disabled={updatingStatus || !newStatus}
          >
            {updatingStatus ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Subscription Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubscription && (
            <Row>
              <Col md={6}>
                <h6>User Information</h6>
                <p>
                  <strong>Name:</strong>{" "}
                  {formatValue(selectedSubscription.user_name)}
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  {formatValue(selectedSubscription.email)}
                </p>
                <p>
                  <strong>Mobile:</strong>{" "}
                  {formatValue(selectedSubscription.mobile)}
                </p>

                <h6 className="mt-4">Subscription Details</h6>
                <p>
                  <strong>ID:</strong> {selectedSubscription.id}
                </p>
                <p>
                  <strong>Plan:</strong>{" "}
                  {getPlanBadge(selectedSubscription.plan_name)}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {getStatusBadge(selectedSubscription.status)}
                </p>
                <p>
                  <strong>Interval:</strong>{" "}
                  {formatValue(selectedSubscription.interval_type)}
                </p>
              </Col>
              <Col md={6}>
                <h6>Billing Information</h6>
                <p>
                  <strong>Amount:</strong>{" "}
                  {formatCurrency(
                    selectedSubscription.amount,
                    selectedSubscription.currency
                  )}
                </p>
                <p>
                  <strong>Currency:</strong>{" "}
                  {formatValue(selectedSubscription.currency)}
                </p>
                <p>
                  <strong>Start Date:</strong>{" "}
                  {formatDate(selectedSubscription.start_date)}
                </p>
                <p>
                  <strong>Next Billing:</strong>{" "}
                  {formatDate(selectedSubscription.next_billing_date)}
                </p>

                <h6 className="mt-4">Usage Information</h6>
                <p>
                  <strong>API Limit:</strong>{" "}
                  {formatValue(
                    selectedSubscription.custom_api_limit ||
                      selectedSubscription.monthly_api_limit
                  )}
                </p>
                <p>
                  <strong>API Used:</strong>{" "}
                  {selectedSubscription.api_limit_used || 0}
                </p>
                <p>
                  <strong>Usage:</strong>{" "}
                  {selectedSubscription.usage_percentage || 0}%
                </p>

                {selectedSubscription.gateway_subscription_id &&
                  selectedSubscription.gateway_subscription_id !== "N/A" && (
                    <>
                      <h6 className="mt-4">Technical Details</h6>
                      <p>
                        <strong>Gateway subscription ID:</strong>{" "}
                        <small>
                          {selectedSubscription.gateway_subscription_id}
                        </small>
                      </p>
                    </>
                  )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubscription && (
            <>
              <p className="mb-2">
                You are about to delete subscription <strong>#{selectedSubscription.id}</strong>.
              </p>
              <ul className="mb-0">
                <li>
                  <strong>User:</strong> {formatValue(selectedSubscription.user_name)} (
                  {formatValue(selectedSubscription.email)})
                </li>
                <li>
                  <strong>Plan:</strong> {formatValue(selectedSubscription.plan_name)}
                </li>
                <li>
                  <strong>Status:</strong> {formatValue(selectedSubscription.status)}
                </li>
              </ul>
              <div className="alert alert-warning mt-3 mb-0">
                This will remove the subscription from the database (and related invoices/payments for that
                subscription). Use only for test cleanup.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deletingSubscription}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteSubscription} disabled={deletingSubscription}>
            {deletingSubscription ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SubscriptionsTab;
